import { GoalMetric, zeroTargets } from "@sp-crm/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useFeature, useNow } from "store/selectors/hooks";
import {
    DisplayMetric,
    displayMetricValue,
    otherActivitiesLabel,
} from "./display-metrics";
import { computeMetricProgress, MetricProgress } from "./metric-progress-calc";
import { currentYearMonth, daysInYearMonth } from "./pace";

export type TrackedMetric = Extract<DisplayMetric, "visit" | "otherTouchpoints">;

export const trackedMetrics: TrackedMetric[] = ["visit", "otherTouchpoints"];

export const trackedMetricLabels: Record<TrackedMetric, string> = {
    visit: "Visits",
    otherTouchpoints: otherActivitiesLabel,
};

const itemMetricsForTrackedMetric: Record<TrackedMetric, GoalMetric[]> = {
    visit: ["visit"],
    otherTouchpoints: ["call", "text", "email"],
};

export interface MetricProgressResult {
    isLoading: boolean;
    isError: boolean;
    progress: MetricProgress | null;
}

export const useMetricProgress = (metric: TrackedMetric): MetricProgressResult => {
    const now = useNow();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const trpcClient = trpc.useTRPC();
    const month = currentYearMonth(now);
    const showingOtherTouchpoints = metric === "otherTouchpoints";

    const attainmentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month }),
    });

    const goal = attainmentQuery.data?.goal ?? zeroTargets();
    const target = displayMetricValue(goal, metric);
    const hasGoal = target > 0;

    const projectionQuery = useQuery({
        ...trpcClient.marketingPlaybook.myTouchpointVolumeProjectionTotal.queryOptions({
            month,
        }),
        enabled: marketingPlaybookEnabled && hasGoal,
    });
    const itemsQuery = useQuery({
        ...trpcClient.marketingGoal.myMetricItems.queryOptions({
            month,
            metrics: itemMetricsForTrackedMetric[metric],
        }),
        placeholderData: keepPreviousData,
    });

    const isLoading =
        attainmentQuery.isLoading ||
        itemsQuery.isLoading ||
        (hasGoal && projectionQuery.isLoading);
    const isError =
        attainmentQuery.isError ||
        itemsQuery.isError ||
        (hasGoal && projectionQuery.isError);

    if (isLoading || isError) {
        return { isLoading, isError, progress: null };
    }

    const projectedMonthly = showingOtherTouchpoints
        ? (projectionQuery.data?.otherTouchpointsVolume ?? 0)
        : (projectionQuery.data?.visitVolume ?? 0);

    const completed = itemsQuery.data?.total ?? 0;
    const daysInMonth = daysInYearMonth(now);
    const progress: MetricProgress = computeMetricProgress(
        completed,
        target,
        projectedMonthly,
        daysInMonth,
        now.getDate(),
    );
    return { isLoading: false, isError: false, progress };
};
